import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, getAssistantHistory } from '../lib/api';
import { streamChat, getOrCreateSession } from '../lib/aiStream';
import { renderChatText } from '../lib/chatText';
import { Sparkles, Send, X } from 'lucide-react';

export const LuxeAssistant = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => getOrCreateSession('alp_luxe_ai_session'));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      getAssistantHistory(sessionId).then((h) => { setMessages((cur) => (cur.length ? cur : (h || []))); setLoaded(true); }).catch(() => setLoaded(true));
    }
  }, [open, loaded, sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: msg }, { role: 'assistant', text: '' }]);
    try {
      await streamChat(`${API}/ai/assistant`, {
        sessionId, message: msg,
        onDelta: (d) => setMessages((m) => {
          const n = [...m];
          const last = n[n.length - 1];
          if (!last || last.role !== 'assistant') return [...n, { role: 'assistant', text: d }];
          n[n.length - 1] = { ...last, text: (last.text || '') + d };
          return n;
        }),
      });
    } catch (e) {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { role: 'assistant', text: 'Sorry, I had trouble answering just now. Please try again.' };
        return n;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[370px] rounded-2xl border border-alp-gold/30 bg-alp-black/95 backdrop-blur-2xl shadow-[0_0_50px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden" data-testid="luxe-assistant-panel" style={{ height: 'min(520px, 70vh)' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-alp-gold/20 bg-alp-card/40">
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-alp-gold" />
              <div>
                <div className="text-alp-cream text-sm font-medium">Luxe Concierge</div>
                <div className="text-alp-gold text-[9px] tracking-[0.25em] uppercase">AI assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="luxe-assistant-close" className="p-1.5 text-alp-gray hover:text-alp-cream transition-colors"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center pt-8 px-4">
                <p className="text-alp-cream/80 text-sm">Hi! Ask me about products, prices, stock, shipping or how ordering works.</p>
                <p className="text-alp-gray text-[10px] mt-3 leading-relaxed">All products are for research purposes only. I can&apos;t provide medical or dosage advice.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div data-testid={`luxe-assistant-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'bg-alp-gold text-alp-black' : 'bg-alp-card border border-alp-line text-alp-cream'}`}>
                  {m.text ? (m.role === 'assistant' ? renderChatText(m.text, (path) => { setOpen(false); navigate(path); }) : m.text) : <span className="inline-flex gap-1 items-center"><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce [animation-delay:240ms]" /></span>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-alp-line p-2.5 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask about a product..." data-testid="luxe-assistant-input"
              className="flex-1 bg-alp-dark border border-alp-line rounded-xl px-3.5 py-2.5 text-[13px] text-alp-cream outline-none focus:border-alp-gold" />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="luxe-assistant-send"
              className="px-4 rounded-xl bg-alp-gold text-alp-black hover:bg-alp-goldlight transition-colors disabled:opacity-40"><Send size={14} /></button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="luxe-assistant-fab"
        className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-alp-gold text-alp-black flex items-center justify-center ai-orb-glow hover:scale-105 transition-transform"
        aria-label="Open Luxe Concierge assistant">
        <Sparkles size={22} />
      </button>
    </>
  );
};
